/** Tiered big win celebration (Amazing / Awesome / Incredible) */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { Spine } from '@esotericsoftware/spine-pixi-v8';
import { AudioManager } from '../audio/AudioManager';
import { SpineManager } from '../animations/SpineManager';
import { Logger } from '../utils/Logger';

const logger = new Logger('BigWinPresentation');

export enum BigWinTier {
  NONE = 'NONE',
  AMAZING = 'AMAZING',
  AWESOME = 'AWESOME',
  INCREDIBLE = 'INCREDIBLE',
}

/** Tier display configuration */
const TIER_CONFIG: Record<string, { label: string; color: number; glowColor: number }> = {
  [BigWinTier.AMAZING]: { label: 'AMAZING WIN!', color: 0x00cc66, glowColor: 0x00ff88 },
  [BigWinTier.AWESOME]: { label: 'AWESOME WIN!', color: 0x3399ff, glowColor: 0x66bbff },
  [BigWinTier.INCREDIBLE]: { label: 'INCREDIBLE WIN!', color: 0xff3366, glowColor: 0xff66aa },
};

/** Map from BigWinTier to the Spine start animation name */
const TIER_SPINE_START: Record<string, string> = {
  [BigWinTier.AMAZING]: 'amazing_start',
  [BigWinTier.AWESOME]: 'awesome_start',
  [BigWinTier.INCREDIBLE]: 'incredible_start',
};

/** Map from BigWinTier to the Spine loop animation name */
const TIER_SPINE_LOOP: Record<string, string> = {
  [BigWinTier.AMAZING]: 'amazing_loop',
  [BigWinTier.AWESOME]: 'awesome_loop',
  [BigWinTier.INCREDIBLE]: 'incredible_loop',
};

/** Manages big win celebration presentations */
export class BigWinPresentation {
  private _isPlaying: boolean = false;
  private _skipped: boolean = false;
  private _overlay: Container | null = null;
  private _amountText: Text | null = null;
  private _resolveShow: (() => void) | null = null;
  private _spineInstance: Spine | null = null;

  get isPlaying(): boolean {
    return this._isPlaying;
  }

  /** Determine the big win tier based on win amount relative to bet */
  static getTier(winAmount: number, totalBet: number): BigWinTier {
    if (totalBet <= 0) return BigWinTier.NONE;
    const multiplier = winAmount / totalBet;
    if (multiplier >= 50) return BigWinTier.INCREDIBLE;
    if (multiplier >= 25) return BigWinTier.AWESOME;
    if (multiplier >= 10) return BigWinTier.AMAZING;
    return BigWinTier.NONE;
  }

  /** Show the big win celebration overlay */
  async show(
    tier: BigWinTier,
    winAmount: number,
    parentContainer: Container,
    designW: number,
    designH: number,
  ): Promise<void> {
    if (tier === BigWinTier.NONE) return;

    const config = TIER_CONFIG[tier];
    if (!config) return;

    logger.info(`Showing ${tier} big win celebration: $${winAmount.toFixed(2)}`);

    this._isPlaying = true;
    this._skipped = false;

    const audio = AudioManager.getInstance();

    // Create overlay container
    const overlay = new Container();
    overlay.zIndex = 2000;
    overlay.eventMode = 'static';
    overlay.cursor = 'pointer';
    this._overlay = overlay;

    // Dimmed background
    const dimBg = new Graphics();
    dimBg.rect(0, 0, designW, designH);
    dimBg.fill({ color: 0x000000, alpha: 0.7 });
    overlay.addChild(dimBg);

    // Tier label text
    const tierText = new Text({
      text: config.label,
      style: new TextStyle({
        fontFamily: 'Arial Black, Arial',
        fontSize: 72,
        fill: config.color,
        stroke: { color: 0x000000, width: 6 },
        dropShadow: {
          color: config.glowColor,
          blur: 16,
          distance: 0,
          angle: 0,
        },
      }),
    });
    tierText.anchor.set(0.5, 0.5);
    tierText.x = designW / 2;
    tierText.y = designH / 2 - 80;
    overlay.addChild(tierText);

    // Win amount text (starts at $0.00)
    const amountText = new Text({
      text: '$0.00',
      style: new TextStyle({
        fontFamily: 'Arial Black, Arial',
        fontSize: 56,
        fill: 0xf7b733,
        stroke: { color: 0x000000, width: 5 },
        dropShadow: {
          color: 0x000000,
          blur: 8,
          distance: 3,
          angle: Math.PI / 4,
        },
      }),
    });
    amountText.anchor.set(0.5, 0.5);
    amountText.x = designW / 2;
    amountText.y = designH / 2 + 30;
    overlay.addChild(amountText);
    this._amountText = amountText;

    // Tap to skip hint
    const skipText = new Text({
      text: 'TAP TO SKIP',
      style: new TextStyle({
        fontFamily: 'Arial, Helvetica',
        fontSize: 20,
        fill: 0xaaaaaa,
      }),
    });
    skipText.anchor.set(0.5, 0.5);
    skipText.x = designW / 2;
    skipText.y = designH / 2 + 120;
    overlay.addChild(skipText);

    // Click/tap to skip handler
    overlay.on('pointerdown', () => {
      this.skip();
    });

    // Attempt to add Spine big_win animation behind text
    const spineManager = SpineManager.getInstance();
    if (spineManager.isLoaded('big_win')) {
      const startAnim = TIER_SPINE_START[tier];
      const loopAnim = TIER_SPINE_LOOP[tier];
      if (startAnim && loopAnim) {
        const spineInst = spineManager.createInstance('big_win');
        if (spineInst) {
          this._spineInstance = spineInst;
          spineInst.x = designW / 2;
          spineInst.y = designH / 2;
          // Insert behind text elements (after dimBg which is index 0)
          overlay.addChildAt(spineInst, 1);
          // Fire-and-forget: play start → loop
          spineManager.playStartThenLoop(spineInst, startAnim, loopAnim).catch(() => {});
        }
      }
    }

    parentContainer.addChild(overlay);

    // Play big win sound
    const bigWinSoundId = audio.playSFX('sfx_win_big');

    // Countup animation (~3 seconds, 60fps = ~180 frames)
    const COUNTUP_DURATION_MS = 3000;
    const COUNTUP_INTERVAL_MS = 16; // ~60fps
    const totalSteps = Math.floor(COUNTUP_DURATION_MS / COUNTUP_INTERVAL_MS);

    await new Promise<void>((resolve) => {
      this._resolveShow = resolve;
      let step = 0;

      const countupInterval = setInterval(() => {
        if (this._skipped || step >= totalSteps) {
          clearInterval(countupInterval);

          // Show final amount
          if (this._amountText) {
            this._amountText.text = `$${winAmount.toFixed(2)}`;
          }

          // Stop big win loop sound and play end sound
          if (bigWinSoundId !== undefined) {
            audio.stopSFX('sfx_win_big', bigWinSoundId);
          }
          audio.playSFX('sfx_win_big_end');

          if (this._skipped) {
            // Skipped — short pause then dismiss
            setTimeout(() => {
              this._cleanup(parentContainer);
              resolve();
            }, 500);
          } else {
            // Natural end — hold for 1.5s then dismiss
            setTimeout(() => {
              this._cleanup(parentContainer);
              resolve();
            }, 1500);
          }
          return;
        }

        step++;
        const progress = step / totalSteps;
        // Ease-out for the countup (decelerating)
        const easedProgress = 1 - Math.pow(1 - progress, 2);
        const currentAmount = winAmount * easedProgress;

        if (this._amountText) {
          this._amountText.text = `$${currentAmount.toFixed(2)}`;
        }
      }, COUNTUP_INTERVAL_MS);
    });

    this._resolveShow = null;
  }

  /** Skip/fast-forward the presentation */
  skip(): void {
    if (!this._isPlaying) return;
    logger.info('Big win presentation skipped');
    this._skipped = true;
  }

  /** Clean up overlay and reset state */
  private _cleanup(parentContainer: Container): void {
    // Return Spine instance to pool before destroying the overlay
    if (this._spineInstance) {
      const spineManager = SpineManager.getInstance();
      spineManager.returnInstance('big_win', this._spineInstance);
      this._spineInstance = null;
    }

    if (this._overlay) {
      parentContainer.removeChild(this._overlay);
      this._overlay.destroy({ children: true });
      this._overlay = null;
    }
    this._amountText = null;
    this._isPlaying = false;
    this._skipped = false;
    logger.info('Big win presentation complete');
  }
}
